// Avatar Creation: a large preview of your avatar and, per category, every
// part drawn on your avatar so you pick by sight. Parts and drawing come from
// the avatars module (web/src/modules/avatars); this is only the picker.
import { one } from './dom.ts';
import { CATEGORIES, randomConfig, type AvatarConfig } from './modules/avatars/catalog.ts';
import { AvatarRenderer } from './modules/avatars/renderer.ts';

// One WebGL context for every avatar on the page (browsers cap live contexts).
let shared: AvatarRenderer | null = null;
export function avatarRenderer(): AvatarRenderer {
  return (shared ??= new AvatarRenderer());
}
// The same, transparent: avatars drawn over the scene (social).
let sharedAlpha: AvatarRenderer | null = null;
export function avatarRendererAlpha(): AvatarRenderer {
  return (sharedAlpha ??= new AvatarRenderer({ alpha: true }));
}

// Keep every canvas.avatar inside `root` animated while root is on the page.
// Each canvas carries its config in .avatarConfig.
export function animateAvatars(root: HTMLElement, every = 2): void {
  const r = avatarRendererAlpha();
  let n = 0;
  const frame = (ms: number) => {
    if (!root.isConnected || root.hidden) return;
    if (n++ % every === 0) {
      for (const c of root.querySelectorAll<HTMLCanvasElement & { avatarConfig?: AvatarConfig }>('canvas.avatar')) {
        if (!c.avatarConfig || !c.clientWidth) continue;
        const dpr = Math.min(devicePixelRatio, 2), w = Math.round(c.clientWidth * dpr);
        if (c.width !== w) { c.width = w; c.height = w; }
        r.draw(c, c.avatarConfig, ms / 1000);
      }
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

export function avatarCanvas(config: AvatarConfig, className = ''): HTMLCanvasElement {
  const c = document.createElement('canvas') as HTMLCanvasElement & { avatarConfig?: AvatarConfig };
  c.className = `avatar ${className}`.trim();
  c.avatarConfig = config;
  c.setAttribute('aria-hidden', 'true');
  return c;
}

type Category = keyof typeof CATEGORIES;

function sized(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const dpr = Math.min(devicePixelRatio, 2);
  canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
  canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
  return canvas;
}

const ICON_DIE = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="4"/><circle cx="9" cy="9" r="1.2"/><circle cx="15" cy="15" r="1.2"/><circle cx="15" cy="9" r="1.2"/><circle cx="9" cy="15" r="1.2"/></svg>';

// Builds the picker inside `host` and animates it while it's on the page.
// onChange fires with every pick; saving is the caller's business.
export function mountCreator(host: HTMLElement, initial: AvatarConfig, onChange: (c: AvatarConfig) => void): { get: () => AvatarConfig } {
  let config = { ...initial };
  host.classList.add('creator');
  host.innerHTML = `<div class="cr-stage"><canvas class="cr-preview"></canvas>
      <button type="button" class="go cr-random" aria-label="⚄">${ICON_DIE}</button></div>
    <div class="cr-parts"></div>`;
  const preview = one<HTMLCanvasElement>(host, '.cr-preview');
  const parts = one(host, '.cr-parts');

  const options = (Object.keys(CATEGORIES) as Category[]).flatMap((category) => {
    const row = document.createElement('div');
    row.className = 'cr-row';
    parts.append(row);
    return CATEGORIES[category].map((id, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cr-opt';
      b.setAttribute('aria-label', String(i + 1)); // its number on Eric's sheet
      b.append(document.createElement('canvas'));
      b.addEventListener('click', () => select({ ...config, [category]: id }));
      row.append(b);
      return { category, id, button: b, canvas: one<HTMLCanvasElement>(b, 'canvas') };
    });
  });

  function select(next: AvatarConfig) {
    config = next;
    for (const o of options) o.button.classList.toggle('on', config[o.category] === o.id);
    onChange(config);
  }
  one(host, '.cr-random').addEventListener('click', () => select(randomConfig()));
  for (const o of options) o.button.classList.toggle('on', config[o.category] === o.id);

  // The preview moves every frame; the options take turns, one row a frame,
  // so phones aren't drawing fifty avatars at once.
  const renderer = avatarRenderer();
  const rows = Object.keys(CATEGORIES) as Category[];
  let n = 0, sizedOnce = false;
  const frame = (ms: number) => {
    if (!host.isConnected) return;
    if (!sizedOnce && preview.clientWidth) { sized(preview); options.forEach((o) => sized(o.canvas)); sizedOnce = true; }
    if (sizedOnce) {
      const t = ms / 1000;
      renderer.draw(preview, config, t);
      const cat = rows[n++ % rows.length];
      for (const o of options) if (o.category === cat) renderer.draw(o.canvas, { ...config, [o.category]: o.id }, t);
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  return { get: () => config };
}
